<template>
  <v-layout wrap id="scheduler-select-service">
    <v-layout wrap fill-width section>
      <v-flex xs12>
        <v-flex xs12>
          <service-selection
            display="columns"
            :menuProps="serviceMenuProps"
            :loadResources="true"
            :enablePayAtCloseBadge="true"
            :field-errors="fieldErrors"
            highlight-add-ons
            show-services-heading
            :active="active"
            @serviceChange="updateServiceIds"
            @addonChange="updateAddOnIds"
          />
        </v-flex>
        <v-flex xs12 md6 class="small" v-if="bnplEnabled">
          <div class="left-margin">
            Buy Now Pay Later Options Available for This Order
          </div>
          <v-layout wrap class="bnpl-providers">
            <div
              class="left-margin"
              v-if="this.company.attributes.settings.enable_bnpl_affirm"
            >
              <img
                alt="affirm"
                role="presentation"
                class="bnpl-img"
                src="https://js.stripe.com/v3/fingerprinted/img/AffirmFullLogo-025e93b73bec0795257fad3a337fde1d.svg"
              />
            </div>
            <div
              class="left-margin"
              v-if="this.company.attributes.settings.enable_bnpl_afterpay"
            >
              <img alt="afterpay" class="bnpl-img" :src="afterpayLogo" />
            </div>
          </v-layout>
          <div class="italic small left-margin bnpl-providers" v-if="this.company.attributes.settings.enable_bnpl_affirm">
            Payment options through Affirm are subject to eligibility, may not be available in all states, and are provided by these lending partners:
            <a href="https://affirm.com/lenders" target="_blank">affirm.com/lenders</a>. CA residents: Loans by Affirm Loan Services, LLC are made or arranged pursuant to a California Finance Lenders Law license.
          </div>
        </v-flex>
      </v-flex>
      <pay-at-close-alert v-if="showPayAtCloseAlert" />
    </v-layout>
    <v-layout wrap fill-width section>
      <v-flex xs12>
        <v-flex xs12>
          <h3>
            <i class="fa fa-map-marker" />
            LOCATION
          </h3>
        </v-flex>
        <v-flex xs12>
          <property-form
            :field-errors="fieldErrors"
            @propertyUpdated="updateProperty"
            @propertyReady="updateProperty"
          />
        </v-flex>
      </v-flex>
    </v-layout>
    <v-layout v-if="hasCustomFields" wrap fill-width section>
      <v-flex xs12 md7>
        <v-layout wrap>
          <v-flex xs12>
            <h3>
              <i class="fa fa-bars" />
              Additional Info
            </h3>
          </v-flex>
          <template
            v-for="(field, index) in inspection.custom_fields_attributes"
          >
            <v-flex
              xs12
              md6
              vpx-1
              class="custom-field-container"
              :key="field.attributes.custom_field_template_id"
              :id="`additional-info-field__${index}`"
            >
              <custom-field
                :field="field"
                v-model="field.attributes.value"
                :field-errors="customFieldErrors(index)"
              />
            </v-flex>
          </template>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-layout>
</template>

<script>
import { mapState } from 'vuex'
import CustomField from '../CustomField'
import fieldErrors from '@/mixins/fieldErrors'
import PropertyForm from '../PropertyForm'
import ServiceSelection from '../ServiceSelection'
import PayAtCloseAlert from './PayAtClose/PayAtCloseAlert'

export default {
  components: {
    CustomField,
    PropertyForm,
    ServiceSelection,
    PayAtCloseAlert,
  },
  mixins: [fieldErrors],
  computed: {
    ...mapState('inspection', [
      'addOns',
      'customFields',
      'services',
      'company',
    ]),
    hasCustomFields() {
      return (this.inspection.custom_fields_attributes || []).length > 0
    },
    serviceMenuProps() {
      return { maxHeight: '200' }
    },
    bnplEnabled() {
      return (
        this.company.attributes.settings.enable_bnpl &&
        this.company.attributes.settings.enable_bnpl_for_all_inspections &&
        (this.company.attributes.settings.enable_bnpl_affirm ||
          this.company.attributes.settings.enable_bnpl_afterpay)
      )
    },
    afterpayLogo() {
      const logo =
        this.company.attributes.country === 'US'
          ? 'CashappAfterpayFullLogo-50ee3da13d3ec9e66cd364d7af5daa8a.svg'
          : 'AfterpayFullLogo-a4d4a1afe4290508f322096846fb9173.svg'

      return `https://js.stripe.com/v3/fingerprinted/img/${logo}`
    },
    canUsePayAtClose() {
      return this.company.attributes.pay_at_close_eligibility
        .can_use_pay_at_close
    },
    showPayAtCloseAlert() {
      const hasPayAtCloseServices = this.services.some(
        (service) => service.attributes.enable_pay_at_close
      )
      return this.canUsePayAtClose && hasPayAtCloseServices
    },
  },
  data() {
    return {
      addonIds: [],
      property: null,
      serviceIds: [],
    }
  },
  methods: {
    // Schedule.vue keys custom-field errors by index (`custom_3`) because one step can hold
    // several. CustomField itself only ever shows one field, so it gets the plain `custom` key.
    customFieldErrors(index) {
      const message = this.fieldError(`custom_${index}`)
      return message ? { custom: message } : {}
    },
    updateAddOnIds(ids) {
      this.addonIds = ids
      this.inspection.add_ons = ids.map((id) => {
        let addon = this.addOns.find((ao) => {
          return ao.attributes.id === id
        })
        return addon
      })
    },
    updateProperty(property) {
      this.$set(this, 'property', property)
      this.inspection.property = property
    },
    updateServiceIds(ids) {
      this.serviceIds = ids
      this.inspection.services = ids.map((id) => {
        let service = this.services.find((s) => {
          return s.attributes.id === id
        })
        return service
      })
    },
  },
  props: ['inspection', 'active'],
}
</script>

<style scoped>
.custom-field-container {
  display: flex;
  align-items: center;
}
.left-margin {
  margin-left: 10px;
}

.bnpl-img {
  max-height: 25px;
}

.bnpl-providers {
  margin-top: 15px;
}
</style>

<style lang="scss">
#scheduler-select-service {
  .property-square-feet label:before,
  .property-year-built label:before {
    content: '* ';
    color: #dc1414;
    font-weight: 300;
  }

  .property-container .toggle.btn-flat {
    display: none;
  }
}

.pay-at-close-alert {
  font-size: 14px;
  color: #374151;
  background-color: #f6f7f8;
  border-radius: 8px;
  padding: 10px 12px;

  &-title {
    font-weight: 600;
    margin-left: 8px;
    margin-bottom: 4px;
  }

  &-description {
    font-weight: 400;
  }
}
</style>
